import {
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { notifyMutationError } from "@/lib/repository-utils";

/** Curator-owned members of the backend Update*Command records. Everything else is TINA's. */
export const LEAGUE_EDITABLE = [
  "id",
  "name",
  "enabled",
  "tier",
  "status",
  "hide",
  "media",
] as const;
export const TEAM_EDITABLE = [
  "id",
  "shortName",
  "city",
  "homeVenueId",
  "foundedYear",
  "nicknames",
  "wikidataId",
  "media",
  "status",
  "hide",
] as const;
/** UpdateMatchCommand: setting venueId makes the venue a Curator venue that /sync never overrides. */
export const MATCH_EDITABLE = [
  "id",
  "status",
  "hide",
  "tags",
  "ticketUrl",
  "pricing",
  "venueId",
] as const;

export function pickEditable(
  values: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of keys) if (key in values) out[key] = values[key];
  return out;
}

export interface LeagueSummary {
  id: string;
  name?: string;
  sport?: string;
  country?: string;
}
export interface TeamSummary {
  id: string;
  name?: string;
  sport?: string;
  city?: string | null;
}

type SportsResource = "leagues" | "teams" | "matches";

export const useSportsRepository = () => {
  const { getList, getOne } = useDataProvider()();
  const { mutateAsync: updateLeagueAsync } = useUpdate({ resource: "leagues" });
  const { mutateAsync: updateTeamAsync } = useUpdate({ resource: "teams" });
  const { mutateAsync: updateMatchAsync } = useUpdate({ resource: "matches" });
  const { edit } = useNavigation();
  const { open } = useNotification();

  async function search<T>(
    resource: SportsResource,
    query: string,
    queryBy: string,
  ): Promise<T[]> {
    if (!query) return [];
    try {
      const response = await getList({
        resource,
        pagination: { currentPage: 0, pageSize: 25 },
        filters: [],
        meta: { sortBy: "_text_match:desc", query, queryBy },
      });
      return response.data.map((hit: any) => hit?.document ?? hit) as T[];
    } catch (ex) {
      console.error(`${resource} search failed due to:`, ex);
      open?.({
        message: `${resource} search failed due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function update(
    resource: SportsResource,
    mutate: typeof updateLeagueAsync,
    keys: readonly string[],
    values: Record<string, any>,
    label: string,
  ) {
    const payload = pickEditable(values, keys);
    const response = await mutate?.(
      { id: values.id, values: payload },
      {
        onSuccess: () => {
          edit(resource, values.id, "replace");
          open?.({ type: "success", message: `Successfully updated ${label}` });
        },
        onError: (error) => {
          console.error(`Failed to update ${label}`, values, error);
          notifyMutationError(open, `Update ${label} request`, error);
        },
      },
    );
    return response?.data.id?.toString();
  }

  return {
    searchLeagues: (q: string) => search<LeagueSummary>("leagues", q, "name"),
    searchTeams: (q: string) =>
      search<TeamSummary>("teams", q, "name,shortName,city"),
    getLeague: (id: string) => getOne({ id, resource: "leagues" }),
    getTeam: (id: string) => getOne({ id, resource: "teams" }),
    getMatch: (id: string) => getOne({ id, resource: "matches" }),
    updateLeague: (v: Record<string, any>) =>
      update("leagues", updateLeagueAsync, LEAGUE_EDITABLE, v, "league"),
    updateTeam: (v: Record<string, any>) =>
      update("teams", updateTeamAsync, TEAM_EDITABLE, v, "team"),
    updateMatch: (v: Record<string, any>) =>
      update("matches", updateMatchAsync, MATCH_EDITABLE, v, "match"),
  };
};
